/**
 * Parse the free-text `concentration` and `size` strings on catalog products
 * into the numeric values the peptide calculator needs (mg/mL concentration +
 * vial volume in mL).
 *
 * Real catalog data is inconsistent — these are the shapes we see in prod:
 *   concentration: "5mg/ml", "20mg/mL", "300mg/ML", "5mg per ml"  (per-mL)
 *                  "5mg", "10mg", "50mg", "9mg powder", "6 mg"     (bare — see below)
 *                  "0.50mg", "1.5mg per tablet"                    (oral dose/unit)
 *                  "Hydroquinone 6%", "B6 2mgCholine 25mg"         (topical / blends)
 *                  "0", "varies", "See Product Page"               (placeholders)
 *   size:          "5mL", "5 mL", "1ml", "0.5ml", "5 ml/ 10 ml"    (vial volume)
 *                  "30ct", "60 Tablets", "1", "varies"             (count — no mL)
 *
 * Per the pharmacy team: a BARE "Xmg" on an *injectable* peptide is a data-entry
 * misname — it means "X mg/mL" (the "/ml" was dropped), NOT total mg in the vial.
 * So on injectables we treat bare mg as per-mL (flagged `assumedPerMl`) so the
 * dose math is correct even before the catalog strings are cleaned up. On
 * non-injectables a bare mg is an oral dose-per-unit and isn't volume-calcable.
 */

export type ConcUnit = 'mg' | 'mcg' | 'units';

export interface ParsedConcentration {
  raw: string;
  /** True when this yields a usable per-mL concentration for injectable dose math. */
  calcReady: boolean;
  /** Numeric magnitude (in `unit`), or null when unsupported. */
  value: number | null;
  unit: ConcUnit | null;
  /** True when `value` is a per-mL concentration. */
  perMl: boolean;
  /** True when a bare "Xmg" on an injectable was interpreted as "X mg/mL". */
  assumedPerMl: boolean;
  /** Why it isn't calc-ready, or the assumption made — for UI hints. */
  note?: string;
}

const PLACEHOLDER = /^(0|0+\.?0*|n\/?a|varies|see\s+product.*|tbd|--?)$/i;

function normUnit(u: string): ConcUnit {
  const s = u.toLowerCase();
  if (s.startsWith('mcg')) return 'mcg';
  if (s.startsWith('unit')) return 'units';
  return 'mg';
}

const PER_ML = /^(\d+(?:\.\d+)?)\s*(mg|mcg|units?)\s*(?:\/\s*m[lL]|per\s*m[lL])$/i;
const BARE = /^(\d+(?:\.\d+)?)\s*(mg|mcg|units?)(?:\s*powder)?$/i;
const PER_TABLET = /^(\d+(?:\.\d+)?)\s*(mg|mcg|units?)\s*per\s*(?:tablet|capsule|cap|tab|lozenge|troche)$/i;

export function parseConcentration(
  raw: string | undefined | null,
  opts?: { injectable?: boolean }
): ParsedConcentration {
  const text = (raw ?? '').trim();
  const base: ParsedConcentration = {
    raw: text, calcReady: false, value: null, unit: null, perMl: false, assumedPerMl: false,
  };

  if (!text || PLACEHOLDER.test(text)) {
    return { ...base, note: 'No concentration specified' };
  }

  let m = text.match(PER_ML);
  if (m) {
    return { ...base, calcReady: true, value: parseFloat(m[1]), unit: normUnit(m[2]), perMl: true };
  }

  m = text.match(PER_TABLET);
  if (m) {
    // Oral strength per unit — not an injectable volume calc.
    return { ...base, value: parseFloat(m[1]), unit: normUnit(m[2]), note: 'Oral dose per unit — not volume-calcable' };
  }

  m = text.match(BARE);
  if (m) {
    const value = parseFloat(m[1]);
    const unit = normUnit(m[2]);
    if (opts?.injectable) {
      // Bare mg on an injectable = misnamed mg/mL (per pharmacy team).
      return { ...base, calcReady: true, value, unit, perMl: true, assumedPerMl: true, note: 'Assumed mg/mL (catalog missing "/ml")' };
    }
    return { ...base, value, unit, note: 'Oral dose — not volume-calcable' };
  }

  // Topicals (%), multi-ingredient blends, ratios like "2.5mg/50mg", etc.
  return { ...base, note: 'Unsupported format (blend, topical, or combination)' };
}

/**
 * Pull a vial volume in mL out of a size string. Returns the first mL value
 * found ("5 ml/ 10 ml" → 5), or null when the size is a count ("30ct") or
 * otherwise has no mL.
 */
export function parseVialMl(size: string | undefined | null): number | null {
  const text = (size ?? '').trim();
  if (!text) return null;
  const m = text.match(/(\d+(?:\.\d+)?)\s*m[lL]\b/);
  return m ? parseFloat(m[1]) : null;
}
